import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from '../components/layout/Header';
import StatCard from '../components/ui/StatCard';
import { toast } from '../components/ui/use-toast';

import {
  UserPlus,
  ClipboardList,
  FileCheck,
  Users,
  GraduationCap,
  Calendar,
  FileBadge,
  School,
  CreditCard,
  Building2,
  BookOpen,
  CheckCircle,
  Clock
} from 'lucide-react';
import CalendarPlus from '../components/ui/CalendarPlus';
import BarChart from '../components/ui/BarChart';
import { getAuth } from "firebase/auth";
import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import { app } from "../firebase";
import { useLoading } from '../hooks/useLoading';

// Hero section animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('students');

  const navigate = useNavigate();
  const db = getFirestore(app);
  const auth = getAuth(app);
  const [courses, setCourses] = useState([]);
  const { setIsLoading } = useLoading();
  const [studentData, setStudentData] = useState(null); // State for student details  

  useEffect(() => {
    const checkUserLoggedIn = async () => {
      setIsLoading(true);
      const userData = JSON.parse(localStorage?.getItem('user')!) ?? null;

      if (userData && userData.accessToken && userData.tokenCreatedAt) {
        try {
          const studentQuery = query(
            collection(db, "users"),
            where("emailId", "==", userData.emailId)
          );
          const studentSnapshot = await getDocs(studentQuery);

         
          const studentDoc = studentSnapshot.docs[0];
          const studentDetails: any = { id: studentDoc.id, ...studentDoc.data() };

          let user:any = JSON.parse(localStorage.getItem("user")!);
          user['role'] = studentDetails?.role;
          localStorage.setItem("user", JSON.stringify(user));

          if (studentDetails?.role !== "MASTERADMIN" &&
            studentDetails?.role !== "ADMIN" && studentDetails?.role !== "SUPERADMIN") {
            setTimeout(() => {
              toast({
                title: "Access Denied",
                description: "Only Admins have access to ByteSplash Registration Portal.",
              });
            }, 2000)
            localStorage.clear();
            window.location.href = "/login";
          }
          setStudentData(studentDetails);
          setIsLoading(false);
        } catch (error) {
          console.error("Error during fetching student or courses:", error);
          navigate("/login");
        }
      } else {
        localStorage.clear();
        navigate("/login");
      }
    };

    checkUserLoggedIn();
  }, [setIsLoading]);


  return (
    <div className="h-full flex flex-col">
      <Header title="Registration Portal" subtitle="Manage student registrations, courses, and more" />

      <div className="flex-1 p-4 md:p-8 overflow-y-auto bg-gray-50/50">
        {/* Hero Section */}
        <motion.div
          className="relative rounded-xl overflow-hidden mb-8 bg-[#0071bd] text-white p-8 md:p-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="absolute inset-0  opacity-10 bg-cover bg-center mix-blend-overlay"></div>
          <div className="relative z-10 max-w -2xl">
            <h1 className="text -2xl md:text-4xl font-bold mb-4">Welcome to ByteSplash Registration Portal</h1>
            <p className="text-lg md:text-xl opacity-90 mb-6">Streamline student registration, course management, and certification processes with our comprehensive platform.</p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" asChild className="font-medium bg-white text-blue-600 hover:bg-blue-50">
                <Link to="/students/register">Register New Student</Link>
              </Button>
              <Button size="lg" asChild variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30">
                <Link to="/courses">Browse Courses</Link>
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <StatCard
              icon={<Users className="h-6 w-6 text-blue-600" />}
              label="Active Students"
              value="2,453"
              change="+12% this month"
              trend="up"
              iconBg="bg-blue-100"
              className="hover:shadow-md transition-shadow"
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <StatCard
              icon={<BookOpen className="h-6 w-6 text-green-600" />}
              label="Active Courses"
              value="42"
              change="+3 this month"
              trend="up"
              iconBg="bg-green-100"
              className="hover:shadow-md transition-shadow"
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <StatCard
              icon={<GraduationCap className="h-6 w-6 text-amber-600" />}
              label="Certifications"
              value="1,289"
              change="+89 this month"
              trend="up"
              iconBg="bg-amber-100"
              className="hover:shadow-md transition-shadow"
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <StatCard
              icon={<CreditCard className="h-6 w-6 text-purple-600" />}
              label="Pending Payments"
              value="₹12.4L"
              change="-5% this month"
              trend="down"
              iconBg="bg-purple-100"
              className="hover:shadow-md transition-shadow"
            />
          </motion.div>
        </motion.div>

        {/* Main Features Tabs */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-3 md:grid-cols-6 mb-6">
              <TabsTrigger value="students">Students</TabsTrigger>
              <TabsTrigger value="courses">Courses</TabsTrigger>
              <TabsTrigger value="batches">Batches</TabsTrigger>
              <TabsTrigger value="certification">Certification</TabsTrigger>
              <TabsTrigger value="employees">Employees</TabsTrigger>
              <TabsTrigger value="lms">LMS</TabsTrigger>
            </TabsList>

            {/* Students Tab */}
            <TabsContent value="students" className="space-y-4">
              <h2 className="text-2xl font-semibold mb-4">Student Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-blue-600" />
                      <span>Student Registration</span>
                    </CardTitle>
                    <CardDescription>Register new students in the system</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Student Registration" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Complete student registration with personal, academic, and fee details.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/students/register">Register Student</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5 text-blue-600" />
                      <span>Draft Registrations</span>
                    </CardTitle>
                    <CardDescription>Manage registration drafts</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Draft Registrations" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">View and complete partially filled registration forms.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/students/draft-list">View Drafts</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <FileCheck className="h-5 w-5 text-blue-600" />
                      <span>Fee Management</span>
                    </CardTitle>
                    <CardDescription>Track and manage student fees</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Fee Management" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">View fee details, pending payments, and payment history.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/students/fee-list">Manage Fees</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </TabsContent>

            {/* Courses Tab */}
            <TabsContent value="courses" className="space-y-4">
              <h2 className="text-2xl font-semibold mb-4">Course Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-bytesplash-blue" />
                      <span>Course Catalog</span>
                    </CardTitle>
                    <CardDescription>Browse all available courses</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Course Catalog" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">View course details, modules, and enrollment statistics.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/courses">View Courses</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <GraduationCap className="h-5 w-5 text-bytesplash-blue" />
                      <span>Create Course</span>
                    </CardTitle>
                    <CardDescription>Add new courses to the system</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Create Course" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Create new courses with modules, materials, and settings.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/courses/create">Create Course</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-bytesplash-blue" />
                      <span>Course Registrations</span>
                    </CardTitle>
                    <CardDescription>View recent course enrollments</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Course Registrations" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Track enrollment statistics and student participation.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/logs/courses">View Registrations</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </TabsContent>

            {/* Batches Tab */}
            <TabsContent value="batches" className="space-y-4">
              <h2 className="text-2xl font-semibold mb-4">Batch Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-bytesplash-blue" />
                      <span>All Batches</span>
                    </CardTitle>
                    <CardDescription>Manage all course batches</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="All Batches" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">View and manage all course batches and schedules.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/batches">View Batches</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <CalendarPlus className="h-5 w-5 text-bytesplash-blue" />
                      <span>Create Batch</span>
                    </CardTitle>
                    <CardDescription>Create a new course batch</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Create Batch" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Set up new batches with schedules and assign instructors.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/batches/create">Create Batch</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-bytesplash-blue" />
                      <span>Batch Schedule</span>
                    </CardTitle>
                    <CardDescription>View class schedules</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Batch Schedule" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Manage batch schedules, classes, and timing.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/batches">View Schedule</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </TabsContent>

            {/* Certification Tab */}
            <TabsContent value="certification" className="space-y-4">
              <h2 className="text-2xl font-semibold mb-4">Certification Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <FileBadge className="h-5 w-5 text-bytesplash-blue" />
                      <span>All Certificates</span>
                    </CardTitle>
                    <CardDescription>Browse all issued certificates</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Certificates" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">View and manage all student certificates and credentials.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/certificates">View Certificates</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <FileCheck className="h-5 w-5 text-bytesplash-blue" />
                      <span>Generate Certificate</span>
                    </CardTitle>
                    <CardDescription>Issue new certificates to students</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Generate Certificate" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Create and issue course completion certificates to students.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/certificates/generate">Generate Certificate</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-bytesplash-blue" />
                      <span>ID Cards</span>
                    </CardTitle>
                    <CardDescription>Manage student ID cards</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="ID Cards" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Generate and manage student identification cards.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/id-card">View ID Cards</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </TabsContent>

            {/* Employees Tab */}
            <TabsContent value="employees" className="space-y-4">
              <h2 className="text-2xl font-semibold mb-4">Employee Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-bytesplash-blue" />
                      <span>All Employees</span>
                    </CardTitle>
                    <CardDescription>Browse all staff members</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="All Employees" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">View and manage all faculty and administrative staff.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/employees">View Employees</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-bytesplash-blue" />
                      <span>Register Employee</span>
                    </CardTitle>
                    <CardDescription>Add new staff members</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Register Employee" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Register new faculty and staff with all required details.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/employees/register">Register Employee</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-bytesplash-blue" />
                      <span>Salary Management</span>
                    </CardTitle>
                    <CardDescription>Manage employee salaries</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Salary Management" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Track salary payments, payslips, and financial records.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/employees/salary">Manage Salaries</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </TabsContent>

            {/* LMS Tab */}
            <TabsContent value="lms" className="space-y-4">
              <h2 className="text-2xl font-semibold mb-4">Learning Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <School className="h-5 w-5 text-bytesplash-blue" />
                      <span>LMS Access</span>
                    </CardTitle>
                    <CardDescription>Manage learning platform access</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="LMS Access" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Control student and instructor access to learning materials.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/lms/manage-access">Manage Access</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5 text-bytesplash-blue" />
                      <span>Access Requests</span>
                    </CardTitle>
                    <CardDescription>Review pending access requests</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="Access Requests" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">View and approve LMS access requests from students.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/lms/waiting-list">View Requests</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <FileCheck className="h-5 w-5 text-bytesplash-blue" />
                      <span>New Access</span>
                    </CardTitle>
                    <CardDescription>Provide new LMS access</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="aspect-video bg-gray-100 rounded-md mb-3 overflow-hidden">
                      <img src="/placeholder.svg" alt="New Access" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-gray-600">Grant new students access to the learning platform.</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/lms/provide-access">Provide Access</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-2xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Link to="/students/register" className="block">
              <Card className="h-full hover:shadow-md hover:border-blue-400/50 transition-all duration-300">
                <CardContent className="flex flex-col items-center justify-center p-4 text-center">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <UserPlus className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-medium">Register Student</h3>
                </CardContent>
              </Card>
            </Link>
            <Link to="/courses/create" className="block">
              <Card className="h-full hover:shadow-md hover:border-blue-400/50 transition-all duration-300">
                <CardContent className="flex flex-col items-center justify-center p-4 text-center">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <BookOpen className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-medium">Create Course</h3>
                </CardContent>
              </Card>
            </Link>
            <Link to="/batches/create" className="block">
              <Card className="h-full hover:shadow-md hover:border-blue-400/50 transition-all duration-300">
                <CardContent className="flex flex-col items-center justify-center p-4 text-center">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-medium">Create Batch</h3>
                </CardContent>
              </Card>
            </Link>
            <Link to="/certificates/generate" className="block">
              <Card className="h-full hover:shadow-md hover:border-blue-400/50 transition-all duration-300">
                <CardContent className="flex flex-col items-center justify-center p-4 text-center">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <FileBadge className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-medium">Generate Certificate</h3>
                </CardContent>
              </Card>
            </Link>
            <Link to="/id-card" className="block">
              <Card className="h-full hover:shadow-md hover:border-blue-400/50 transition-all duration-300">
                <CardContent className="flex flex-col items-center justify-center p-4 text-center">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <Building2 className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-medium">ID Card Management</h3>
                </CardContent>
              </Card>
            </Link>
            <Link to="/reports" className="block">
              <Card className="h-full hover:shadow-md hover:border-blue-400/50 transition-all duration-300">
                <CardContent className="flex flex-col items-center justify-center p-4 text-center">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <BarChart className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-medium">View Reports</h3>
                </CardContent>
              </Card>
            </Link>
          </div>
        </motion.div>

        {/* Recent Activity and System Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest updates from the system</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  <li className="flex items-start space-x-3 pb-3 border-b border-gray-100">
                    <span className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                      <UserPlus className="h-4 w-4 text-blue-600" />
                    </span>
                    <div>
                      <p className="text-sm font-medium">New student registration</p>
                      <p className="text-xs text-gray-500">Rahul Sharma registered for Web Development course</p>
                      <p className="text-xs text-gray-500">10 minutes ago</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3 pb-3 border-b border-gray-100">
                    <span className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                      <CreditCard className="h-4 w-4 text-green-600" />
                    </span>
                    <div>
                      <p className="text-sm font-medium">Payment received</p>
                      <p className="text-xs text-gray-500">₹24,500 received from Priya Patel</p>
                      <p className="text-xs text-gray-500">1 hour ago</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center mt-0.5">
                      <FileBadge className="h-4 w-4 text-amber-600" />
                    </span>
                    <div>
                      <p className="text-sm font-medium">Certificate issued</p>
                      <p className="text-xs text-gray-500">Full Stack Development certificate issued to Ankit Singh</p>
                      <p className="text-xs text-gray-500">3 hours ago</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="h-full">
              <CardHeader>
                <CardTitle>System Status</CardTitle>
                <CardDescription>Current system health and metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Server Status</span>
                      <span className="text-sm text-green-600 font-medium">Operational</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: '98%' }}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Database Load</span>
                      <span className="text-sm text-amber-600 font-medium">Moderate</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: '65%' }}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">API Uptime</span>
                      <span className="text-sm text-green-600 font-medium">99.9%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: '99.9%' }}></div>
                    </div>
                  </div>

                  <div className="pt-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Updates</h4>
                    <div className="text-xs text-gray-500 space-y-1">
                      <p>• System maintenance completed successfully - 2 days ago</p>
                      <p>• Database backup performed - 1 day ago</p>
                      <p>• Security patches applied - 12 hours ago</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
